import React, { useRef } from 'react';
import styled, { withTheme } from 'styled-components';
import { useSpring, animated } from 'react-spring';

import { translate, nf } from '~/utils';

const LabelWrapper = styled(animated.g)`
  text {
    font-weight: 700;
    fill: ${(p) => p.theme.colors.highlight};
  }
`;

const Line = styled.line`
  stroke: ${(p) => p.theme.colors.greyblue};
  stroke-width: 1;
  shape-rendering: crispEdges;
`;

const LineLabel = styled.text`
  font-weight: bold;
`;

const LineSideLabel = styled.text`
  fill: ${(p) => p.theme.colors.gray};
  transition: opacity 400ms;
`;

const TextLabel = ({ text, fillOpacity = 1, ...rest }) => (
  <>
    <text
      strokeWidth={3}
      stroke="#fff"
      fillOpacity={fillOpacity}
      strokeOpacity={fillOpacity === 0 ? 0 : 0.8}
      {...rest}
    >
      {text}
    </text>
    <text fillOpacity={fillOpacity} {...rest}>
      {text}
    </text>
  </>
);

const VerticalLine = ({
  data,
  height,
  xScale,
  yScale,
  format,
  radius = 3,
  theme,
  ySelector,
  isRelView,
  isZoomView,
  tooltipPercentile,
}) => {
  const prevRelView = useRef(false);
  const xValue = data[config.columns.PERCENTAGE];
  const x = xScale(xValue);
  const yValue = data[ySelector];
  const yLabel = nf(yValue, format);
  const tooltipDistance = Math.abs(tooltipPercentile - xValue);
  const labelVisible = tooltipDistance > 15 ? 1 : 0;

  const y = useSpring({
    translate: translate(0, yScale(yValue)),
    // we only want an animation when we switch from rel view to non rel view
    // duration: undefined means that a spring animation is used
    config: {
      duration: prevRelView.current !== isRelView || isZoomView ? undefined : 0,
    },
  });

  prevRelView.current = isRelView;

  const text = `${yLabel} ${isRelView ? 'Prozent' : 'Euro'}`;
  const leftInverted =
    (xValue < 40 && !isZoomView) || (xValue < 5 && isZoomView);
  const rightInverted =
    (xValue > 60 && !isZoomView) || (xValue > 95 && isZoomView);

  return (
    <>
      <g transform={translate(x, 0)}>
        <LineLabel dy={-5} textAnchor="middle">
          {xValue !== 100 && xValue}
        </LineLabel>
        <Line x1={0} x2={0} y1={0} y2={height} />

        <LineSideLabel
          textAnchor={leftInverted ? 'start' : 'end'}
          transform={translate(leftInverted ? 10 : -10, leftInverted ? 50 : 10)}
        >
          <tspan>← dieses und niedrigere </tspan>
          <tspan dy="1em" x={0}>
            Einkommen zahlen{' '}
          </tspan>
          <tspan dy="1em" x={0}>
            <tspan fontWeight="bold" fill={theme.colors.highlight}>
              {nf(data[config.columns.LOWER])} Prozent
            </tspan>{' '}
            der
          </tspan>
          <tspan dy="1em" x={0}>
            Einkommensteuer
          </tspan>
        </LineSideLabel>

        <LineSideLabel
          textAnchor={rightInverted ? 'end' : 'start'}
          transform={translate(
            rightInverted ? -10 : 10,
            rightInverted ? 65 : 10
          )}
        >
          <tspan>höhere Einkommen →</tspan>
          <tspan dy="1em" x={0}>
            zahlen{' '}
            <tspan fontWeight="bold" fill={theme.colors.highlight}>
              {nf(data[config.columns.HIGHER])} Prozent
            </tspan>
            <tspan dy="1em" x={0}>
              der Einkommensteuer
            </tspan>
          </tspan>
        </LineSideLabel>

        <LabelWrapper transform={y.translate}>
          <TextLabel
            text={text}
            x={3}
            y={-4}
            fillOpacity={labelVisible}
            textAnchor={xValue > 75 && !isZoomView ? 'end' : 'start'}
          />
          <circle fill={theme.colors.highlight} cx={0} cy={0} r={radius} />
        </LabelWrapper>
      </g>
    </>
  );
};

export default withTheme(VerticalLine);
